import { MessageOutput } from '@quadminds/client-apis/agents';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Attachment } from '../components/pages/Chat/Chat/types';
import { useChatContext } from '../providers/Chat/Chat.provider';
import ChatService from '../services/chat.service';

export function useSendLogisticsMessage(
    optimisticRequest?: (message: string) => void,
    callbackResponse?: ({
        message,
        attachments,
        interactionId,
        conversationId,
        rating,
    }: {
        message: string;
        attachments?: Attachment[];
        interactionId?: string;
        conversationId?: string;
        rating?: string | null;
    }) => void,
    finallyCallback?: () => void,
) {
    const chatService = new ChatService();
    const queryClient = useQueryClient();
    const { setConversationId, conversationId } = useChatContext();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const errorsActions = {
        REQUEST_LIMIT_REACHED: '/error/limit-exceeded',
        GEOCODE_LIMIT_REACHED: '/error/geo-limit',
    };

    return useMutation({
        mutationFn: (message: MessageOutput) => chatService.sendLogisticsMessage(message),
        onMutate(message: MessageOutput) {
            // Handle optimistic update for user message
            if (optimisticRequest) optimisticRequest(message.query);
        },
        onSuccess(data, _variables, _context) {
            // Set conversation ID for new conversations
            if (conversationId === undefined) {
                setConversationId(data.conversation_id);

                setTimeout(() => {
                    queryClient.invalidateQueries({ queryKey: ['conversations'] });
                }, 10000);
            }

            // Handle agent response callback
            if (callbackResponse) {
                callbackResponse({
                    message: data.agent_response,
                    attachments: data?.attachments || undefined,
                    interactionId: data.interaction_id,
                    conversationId: data.conversation_id,
                    rating: null,
                });
            }
        },
        onError(error, _variables, _context) {
            if (error && (error as Error & { statusCode: string })?.statusCode) {
                const statusCode = (error as Error & { statusCode: string }).statusCode;
                if (Object.keys(errorsActions).includes(statusCode)) {
                    navigate(errorsActions[statusCode as keyof typeof errorsActions]);
                }
            }
            if (callbackResponse)
                callbackResponse({
                    message: t('errors.mindyGenericErrorResponse'),
                    attachments: undefined,
                    interactionId: undefined,
                    conversationId: undefined,
                    rating: null,
                });
            console.error('Error', error);
        },
        onSettled(_data, _error) {
            if (finallyCallback) finallyCallback();
        },
    });
}
